/**
 * RICH LINE TEXT — bold, italic, bullets, numbers. Nothing else.
 *
 * Faster Estimates, decision 5 (Corbin 2026-10-15): a quote line's words may be
 * **bold**, *italic*, a bulleted list or a numbered list. No colors, no sizes,
 * no links. The same four styles render on the quote PDF, the public estimate
 * page, the email and the portal; the tech's scope and every plain-text reader
 * keep reading `estimate_lines.description`, which is always the plain copy.
 *
 * The server is the gate. Whatever the browser sends is parsed into a tiny
 * model — paragraphs and lists of styled runs — and written back out in one
 * canonical shape, so the stored HTML can only ever contain:
 *
 *     <p> <ul> <ol> <li> <b> <i> <br>
 *
 * with no attributes. Unknown tags are unwrapped (their words kept); script,
 * style and similar tags are dropped with their contents; entities are decoded
 * and re-escaped. Lists do not nest — a nested list flattens into its parent.
 *
 * CLIENT MIRROR of src/server/services/richLineText.ts — keep the two in step.
 * The server is the real gate (it sanitizes on save); this copy lets the editor
 * show exactly what will be stored and renders stored copies as React nodes,
 * never as innerHTML.
 */

export interface RichRun {
    text: string;
    b?: boolean;
    i?: boolean;
    /** A line break inside a paragraph or list item. */
    br?: boolean;
}

export type RichBlock = { type: "p"; runs: RichRun[] } | { type: "ul" | "ol"; items: RichRun[][] };

type Node = { t: "text"; v: string } | { t: "el"; tag: string; kids: Node[] };

/** Contents dropped entirely, not just the tag. */
const DROP_WITH_CONTENT = new Set([
    "script",
    "style",
    "iframe",
    "object",
    "embed",
    "template",
    "noscript",
    "svg",
    "math",
    "head",
    "title",
    "textarea",
    "select",
    "button",
]);

const TAG_ALIAS: Record<string, string> = {
    b: "b",
    strong: "b",
    i: "i",
    em: "i",
    ul: "ul",
    ol: "ol",
    li: "li",
    p: "p",
    div: "p",
    h1: "p",
    h2: "p",
    h3: "p",
    h4: "p",
    h5: "p",
    h6: "p",
    blockquote: "p",
    br: "br",
};

const NAMED: Record<string, string> = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
    nbsp: " ",
    rdquo: "”",
    ldquo: "“",
    rsquo: "’",
    lsquo: "‘",
    ndash: "–",
    mdash: "—",
    bull: "•",
    hellip: "…",
    frac12: "½",
    frac14: "¼",
    frac34: "¾",
    deg: "°",
    times: "×",
};

export function decodeEntities(s: string): string {
    return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (m, body: string) => {
        if (body[0] === "#") {
            const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return "";
            if (code < 32 && code !== 9 && code !== 10 && code !== 13) return "";
            try {
                return String.fromCodePoint(code);
            } catch {
                return "";
            }
        }
        const v = NAMED[body.toLowerCase()];
        return v ?? m;
    });
}

export function escapeHtml(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

const TOKEN_RE = /<!--[\s\S]*?(?:-->|$)|<\/?([a-zA-Z][a-zA-Z0-9]*)\b(?:[^>"']|"[^"]*"|'[^']*')*>|<|[^<]+/g;

/** HTML → a tree of allowed elements; everything else unwrapped or dropped. */
function parse(html: string): Node[] {
    const root: Node & { t: "el" } = { t: "el", tag: "#root", kids: [] };
    const stack: Array<Node & { t: "el" }> = [root];
    let drop: string | null = null;
    let dropDepth = 0;
    for (const m of html.matchAll(TOKEN_RE)) {
        const tok = m[0];
        if (tok.startsWith("<!--")) continue;
        const name = m[1]?.toLowerCase();
        if (name) {
            const closing = tok.startsWith("</");
            const selfClosing = /\/\s*>$/.test(tok);
            if (drop) {
                if (name === drop) dropDepth += closing ? -1 : selfClosing ? 0 : 1;
                if (dropDepth <= 0) drop = null;
                continue;
            }
            if (DROP_WITH_CONTENT.has(name)) {
                if (!closing && !selfClosing) {
                    drop = name;
                    dropDepth = 1;
                }
                continue;
            }
            const tag = TAG_ALIAS[name];
            if (!tag) continue; // unknown tag: unwrap, keep the words
            if (tag === "br") {
                if (!closing) stack[stack.length - 1].kids.push({ t: "el", tag: "br", kids: [] });
                continue;
            }
            if (closing) {
                const at = stack.map((n) => n.tag).lastIndexOf(tag);
                if (at > 0) stack.length = at;
                continue;
            }
            if (selfClosing) continue;
            const el: Node & { t: "el" } = { t: "el", tag, kids: [] };
            stack[stack.length - 1].kids.push(el);
            stack.push(el);
            continue;
        }
        if (drop) continue;
        stack[stack.length - 1].kids.push({ t: "text", v: tok === "<" ? "<" : decodeEntities(tok) });
    }
    return root.kids;
}

/** Trim whitespace and line breaks off both ends of a run list; drop empties. */
function tidyRuns(runs: RichRun[]): RichRun[] {
    const out: RichRun[] = [];
    for (const r of runs) {
        if (r.br) {
            out.push({ text: "", br: true });
            continue;
        }
        const text = r.text.replace(/[ \t\r\n\f\v\u00a0]+/g, " ");
        if (!text) continue;
        const prev = out[out.length - 1];
        if (prev && !prev.br && !!prev.b === !!r.b && !!prev.i === !!r.i) prev.text += text;
        else out.push({ text, ...(r.b ? { b: true } : {}), ...(r.i ? { i: true } : {}) });
    }
    // Collapse a space that follows a space across a style boundary or a break.
    for (let k = 1; k < out.length; k++) {
        const prev = out[k - 1];
        if (out[k].br || prev.br) {
            if (out[k].br && prev.text.endsWith(" ")) prev.text = prev.text.replace(/ +$/, "");
            if (prev.br) out[k].text = out[k].text.replace(/^ +/, "");
            continue;
        }
        if (prev.text.endsWith(" ") && out[k].text.startsWith(" ")) out[k].text = out[k].text.slice(1);
    }
    while (out.length && (out[0].br || !out[0].text.trim())) out.shift();
    while (out.length && (out[out.length - 1].br || !out[out.length - 1].text.trim())) out.pop();
    if (out.length) {
        out[0].text = out[0].text.replace(/^ +/, "");
        out[out.length - 1].text = out[out.length - 1].text.replace(/ +$/, "");
    }
    // Two breaks in a row read as one.
    return out.filter((r, k) => !(r.br && out[k - 1]?.br) && (r.br || r.text !== ""));
}

/** The tree → paragraphs and flat lists of styled runs. */
export function richBlocksFromHtml(html: string | null | undefined): RichBlock[] {
    if (!html) return [];
    const blocks: RichBlock[] = [];
    let para: RichRun[] | null = null;
    let list: { type: "ul" | "ol"; items: RichRun[][] } | null = null;
    let item: RichRun[] | null = null;

    const flushPara = () => {
        if (para) {
            const runs = tidyRuns(para);
            if (runs.length) blocks.push({ type: "p", runs });
        }
        para = null;
    };
    const flushItem = () => {
        if (list && item) {
            const runs = tidyRuns(item);
            if (runs.length) list.items.push(runs);
        }
        item = null;
    };
    const push = (run: RichRun) => {
        if (list) {
            if (!item) {
                if (!run.br && run.text.trim() === "") return;
                item = [];
            }
            item.push(run);
            return;
        }
        if (!para) {
            if (!run.br && run.text.trim() === "") return;
            para = [];
        }
        para.push(run);
    };

    const walk = (nodes: Node[], b: boolean, i: boolean) => {
        for (const n of nodes) {
            if (n.t === "text") {
                push({ text: n.v, ...(b ? { b: true } : {}), ...(i ? { i: true } : {}) });
                continue;
            }
            switch (n.tag) {
                case "b":
                    walk(n.kids, true, i);
                    break;
                case "i":
                    walk(n.kids, b, true);
                    break;
                case "br":
                    push({ text: "", br: true });
                    break;
                case "p":
                    if (list) {
                        if (item && item.length) item.push({ text: "", br: true });
                        walk(n.kids, b, i);
                    } else {
                        flushPara();
                        walk(n.kids, b, i);
                        flushPara();
                    }
                    break;
                case "ul":
                case "ol":
                    if (list) {
                        flushItem();
                        walk(n.kids, b, i); // nested list flattens into its parent
                        flushItem();
                    } else {
                        flushPara();
                        list = { type: n.tag, items: [] };
                        walk(n.kids, b, i);
                        flushItem();
                        if (list.items.length) blocks.push(list);
                        list = null;
                    }
                    break;
                case "li":
                    if (list) {
                        flushItem();
                        item = [];
                        walk(n.kids, b, i);
                        flushItem();
                    } else {
                        flushPara();
                        walk(n.kids, b, i);
                        flushPara();
                    }
                    break;
                default:
                    walk(n.kids, b, i);
            }
        }
    };
    walk(parse(String(html).slice(0, 50_000)), false, false);
    flushItem();
    flushPara();
    return blocks;
}

function runsToHtml(runs: RichRun[]): string {
    return runs
        .map((r) => {
            if (r.br) return "<br>";
            let s = escapeHtml(r.text);
            if (r.i) s = `<i>${s}</i>`;
            if (r.b) s = `<b>${s}</b>`;
            return s;
        })
        .join("");
}

export function richBlocksToHtml(blocks: RichBlock[]): string {
    return blocks
        .map((blk) =>
            blk.type === "p"
                ? `<p>${runsToHtml(blk.runs)}</p>`
                : `<${blk.type}>${blk.items.map((it) => `<li>${runsToHtml(it)}</li>`).join("")}</${blk.type}>`,
        )
        .join("");
}

/** The canonical, safe HTML for whatever came in. "" when nothing is left. */
export function sanitizeRichHtml(html: string | null | undefined): string {
    return richBlocksToHtml(richBlocksFromHtml(html));
}

/** True when the words carry any style a plain line can't: bold, italic, a
 *  list, a line break, or more than one paragraph. */
export function richHasFormatting(blocks: RichBlock[]): boolean {
    if (blocks.length !== 1) return blocks.length > 1;
    const b = blocks[0];
    if (b.type !== "p") return true;
    return b.runs.some((r) => r.b || r.i || r.br);
}

function runsToPlain(runs: RichRun[]): string {
    return runs.map((r) => (r.br ? "\n" : r.text)).join("");
}

/** The plain-words copy: bullets become "• ", numbers "1. ", one per line. */
export function richBlocksToPlain(blocks: RichBlock[]): string {
    const out: string[] = [];
    for (const blk of blocks) {
        if (blk.type === "p") out.push(runsToPlain(blk.runs));
        else blk.items.forEach((it, k) => out.push(`${blk.type === "ol" ? `${k + 1}.` : "•"} ${runsToPlain(it)}`));
    }
    return out.join("\n").trim();
}

export function richToPlain(html: string | null | undefined): string {
    return richBlocksToPlain(richBlocksFromHtml(html));
}

/**
 * What a save stores. `html` is null when the words carry no styling, so a
 * plain line stays a plain line; `plain` is always the description column.
 */
export function richLineForSave(html: string | null | undefined): { html: string | null; plain: string } {
    const blocks = richBlocksFromHtml(html);
    return {
        html: richHasFormatting(blocks) ? richBlocksToHtml(blocks) : null,
        plain: richBlocksToPlain(blocks),
    };
}

/** Map every text run through `fn` (customer hygiene), keeping the styling. */
export function mapRichText(blocks: RichBlock[], fn: (text: string, first: boolean) => string): RichBlock[] {
    let first = true;
    const mapRuns = (runs: RichRun[]) =>
        runs.map((r) => {
            if (r.br) return r;
            const text = fn(r.text, first);
            first = false;
            return { ...r, text };
        });
    return blocks.map((b) =>
        b.type === "p" ? { ...b, runs: mapRuns(b.runs) } : { ...b, items: b.items.map(mapRuns) },
    );
}
